import { Injectable, Optional, Inject } from '@angular/core';
import { DateAdapter, MAT_DATE_LOCALE } from '@angular/material/core';
import dayjs from 'dayjs/esm';
import customParseFormat from 'dayjs/esm/plugin/customParseFormat';
import localizedFormat from 'dayjs/esm/plugin/localizedFormat';
import localeData from 'dayjs/esm/plugin/localeData';
import utc from 'dayjs/esm/plugin/utc';

type Dayjs = dayjs.Dayjs;

// Extend dayjs with required plugins
dayjs.extend(customParseFormat);
dayjs.extend(localizedFormat);
dayjs.extend(localeData);
dayjs.extend(utc);

/**
 * Custom DateAdapter for Day.js integration with Angular Material.
 * Implements all required DateAdapter methods to support Material date pickers and datetime components.
 *
 * TODO: Add UTC mode support for future backend timezone migration.
 * The UTC plugin is already loaded and can be enabled by setting useUtc option.
 */
@Injectable()
export class DayJsDateAdapter extends DateAdapter<Dayjs> {
  private _localeData: {
    firstDayOfWeek: number;
    longMonths: string[];
    shortMonths: string[];
    narrowMonths: string[];
    dates: string[];
    longDaysOfWeek: string[];
    shortDaysOfWeek: string[];
    narrowDaysOfWeek: string[];
  };

  constructor(@Optional() @Inject(MAT_DATE_LOCALE) matDateLocale: string) {
    super();
    // Initialize with English locale (default)
    this.setLocale(matDateLocale || 'en');
  }

  setLocale(locale: string): void {
    super.setLocale(locale);

    // Set Day.js global locale
    dayjs.locale(locale);

    // Cache locale data for performance
    const localeData = (dayjs() as any).localeData();
    this._localeData = {
      firstDayOfWeek: localeData.firstDayOfWeek(),
      longMonths: localeData.months(),
      shortMonths: localeData.monthsShort(),
      narrowMonths: localeData.monthsShort().map((m: string) => m.substring(0, 1)),
      dates: Array.from({ length: 31 }, (_, i) => String(i + 1)),
      longDaysOfWeek: localeData.weekdays(),
      shortDaysOfWeek: localeData.weekdaysShort(),
      narrowDaysOfWeek: localeData.weekdaysMin(),
    };
  }

  getYear(date: Dayjs): number {
    return date.year();
  }

  getMonth(date: Dayjs): number {
    return date.month();
  }

  getDate(date: Dayjs): number {
    return date.date();
  }

  getDayOfWeek(date: Dayjs): number {
    return date.day();
  }

  getMonthNames(style: 'long' | 'short' | 'narrow'): string[] {
    if (style === 'long') return this._localeData.longMonths;
    if (style === 'short') return this._localeData.shortMonths;
    return this._localeData.narrowMonths;
  }

  getDateNames(): string[] {
    return this._localeData.dates;
  }

  getDayOfWeekNames(style: 'long' | 'short' | 'narrow'): string[] {
    if (style === 'long') return this._localeData.longDaysOfWeek;
    if (style === 'short') return this._localeData.shortDaysOfWeek;
    return this._localeData.narrowDaysOfWeek;
  }

  getYearName(date: Dayjs): string {
    return date.format('YYYY');
  }

  getFirstDayOfWeek(): number {
    return this._localeData.firstDayOfWeek;
  }

  getNumDaysInMonth(date: Dayjs): number {
    return date.daysInMonth();
  }

  clone(date: Dayjs): Dayjs {
    return date.clone();
  }

  createDate(year: number, month: number, date: number): Dayjs {
    // Month is 0-indexed in Day.js (same as moment)
    const result = dayjs().year(year).month(month).date(date).startOf('day');

    if (!this.isValid(result)) {
      throw Error(`Invalid date: year=${year}, month=${month}, date=${date}`);
    }

    return result;
  }

  today(): Dayjs {
    return dayjs();
  }

  parse(value: any, parseFormat: string | string[]): Dayjs | null {
    if (!value) return null;

    // If value is already a Dayjs object, return a clone
    if (dayjs.isDayjs(value)) {
      return value.clone();
    }

    // Handle array of formats
    const formats = Array.isArray(parseFormat) ? parseFormat : [parseFormat];

    // Try parsing with each format
    for (const format of formats) {
      const parsed = dayjs(value, format, true); // true = strict parsing
      if (parsed.isValid()) {
        return parsed;
      }
    }

    // If no format worked, try default parsing (lenient)
    const parsed = dayjs(value);
    return parsed.isValid() ? parsed : null;
  }

  format(date: Dayjs, displayFormat: string): string {
    if (!this.isValid(date)) {
      throw Error('DayJsDateAdapter: Cannot format invalid date.');
    }
    return date.format(displayFormat);
  }

  addCalendarYears(date: Dayjs, years: number): Dayjs {
    return date.add(years, 'year');
  }

  addCalendarMonths(date: Dayjs, months: number): Dayjs {
    return date.add(months, 'month');
  }

  addCalendarDays(date: Dayjs, days: number): Dayjs {
    return date.add(days, 'day');
  }

  toIso8601(date: Dayjs): string {
    return date.toISOString();
  }

  /**
   * Deserialize a value into a Dayjs object.
   * Accepts: Dayjs objects, Date objects, ISO 8601 strings, timestamps.
   * Returns null for empty strings or invalid values.
   */
  deserialize(value: any): Dayjs | null {
    if (!value) return null;

    // Empty string
    if (typeof value === 'string' && value.trim() === '') {
      return null;
    }

    // Already a Dayjs object
    if (dayjs.isDayjs(value)) {
      return value.clone();
    }

    // Date object
    if (value instanceof Date) {
      return dayjs(value);
    }

    // String or number (timestamp)
    if (typeof value === 'string' || typeof value === 'number') {
      const parsed = dayjs(value);
      return parsed.isValid() ? parsed : null;
    }

    return null;
  }

  isDateInstance(obj: any): boolean {
    return dayjs.isDayjs(obj);
  }

  isValid(date: Dayjs): boolean {
    return dayjs.isDayjs(date) && date.isValid();
  }

  invalid(): Dayjs {
    return dayjs(NaN);
  }

  // Time-related methods (required for datetime picker support)

  /**
   * Set time components on a date.
   * Returns a new Dayjs instance with the updated time.
   */
  setTime(target: Dayjs, hours: number, minutes: number, seconds: number): Dayjs {
    return target.hour(hours).minute(minutes).second(seconds);
  }

  /**
   * Get hours component from date (0-23).
   */
  getHours(date: Dayjs): number {
    return date.hour();
  }

  /**
   * Get minutes component from date (0-59).
   */
  getMinutes(date: Dayjs): number {
    return date.minute();
  }

  /**
   * Get seconds component from date (0-59).
   */
  getSeconds(date: Dayjs): number {
    return date.second();
  }

  /**
   * Parse a time value using the given format(s).
   * Similar to parse() but specifically for time values.
   */
  parseTime(value: any, parseFormat: string | string[]): Dayjs | null {
    return this.parse(value, parseFormat);
  }

  /**
   * Add seconds to a date.
   * Returns a new Dayjs instance with the updated time.
   */
  addSeconds(date: Dayjs, amount: number): Dayjs {
    return date.add(amount, 'second');
  }
}
